<script setup lang="ts">
import type { FinancingDraft } from '@/components/financing-requests/financingDraft'
import FinestAmountInput from '@/components/FinestAmountInput.vue'
import FinestCalcRow from '@/components/FinestCalcRow.vue'
import FinestCard from '@/components/FinestCard.vue'
import type { PositionColumn } from '@/components/finest-custom-position-list/types'
import FinestCustomPositionListCard from '@/components/FinestCustomPositionListCard.vue'
import FinestField from '@/components/FinestField.vue'
import FinestInputNumber from '@/components/FinestInputNumber.vue'
import FinestSelect from '@/components/FinestSelect.vue'
import PropertyStepErrors from '@/components/portfolio/PropertyStepErrors.vue'
import { counterpart, roundToCents } from '@/utils/amount-unit'
import { missingWhen } from '@/utils/field-state'
import { additionalPurchaseCostKeys, additionalPurchaseCosts } from '@/utils/investment'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import LoanType = App.Enums.FinancingRequest.LoanType
import RealEstateImprovement = App.Enums.RealEstateImprovement

const amountKeys = additionalPurchaseCostKeys

type AmountKey = (typeof amountKeys)[number]

const requiredAmountKeys: AmountKey[] = ['land_register_charge', 'notary_land_registry_fees']

const props = withDefaults(
  defineProps<{
    improvementTypes: RealEstateImprovement[]
    loanTypes: LoanType[]
    errors: string[]
    showMissing?: boolean
  }>(),
  { showMissing: false },
)

const draft = defineModel<FinancingDraft>({ required: true })

const { t, n, locale } = useI18n()

const isRequired = (key: AmountKey): boolean => requiredAmountKeys.includes(key)

const isMissing = (value: number | string | null): boolean => props.showMissing && value === null

const isAmountMissing = (key: AmountKey): boolean => isRequired(key) && isMissing(draft.value[key])

const additionalCosts = computed(() =>
  additionalPurchaseCosts(
    draft.value.purchase_price,
    {
      land_register_charge: draft.value.land_register_charge,
      broker_commission: draft.value.broker_commission,
      notary_land_registry_fees: draft.value.notary_land_registry_fees,
    },
    {
      land_register_charge: draft.value.land_register_charge_unit,
      broker_commission: draft.value.broker_commission_unit,
      notary_land_registry_fees: draft.value.notary_land_registry_fees_unit,
    },
  ),
)

const modernizationCosts = computed(() =>
  draft.value.modernizations.reduce((total, row) => total + (Number(row.cells.price) || 0), 0),
)

const totalInvestment = computed(
  () => (draft.value.purchase_price ?? 0) + additionalCosts.value + modernizationCosts.value,
)

const amountToFinance = computed(() => totalInvestment.value - (draft.value.equity ?? 0))

const euro = (value: number | null): string =>
  value === null ? '—' : n(value, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })

/** The three money rows read as one split, so each one carries its share of the total investment. */
const shareOfTotal = (value: number | null): string | null => {
  if (value === null || totalInvestment.value <= 0) {
    return null
  }

  return `${n((value / totalInvestment.value) * 100, { maximumFractionDigits: 1 })} %`
}

/**
 * Equity and financing amount split the same total, so entering one fills the other. Writing both from
 * one handler keeps the pair in sync without a watcher that would fight the field the user is typing in.
 */
const counterAmount = (value: number | null): number => roundToCents(Math.max(totalInvestment.value - (value ?? 0), 0))

const setEquity = (value: number | null): void => {
  draft.value.equity = value

  if (totalInvestment.value > 0) {
    draft.value.total_financing_amount = counterAmount(value)
  }
}

const setFinancingAmount = (value: number | null): void => {
  draft.value.total_financing_amount = value

  if (totalInvestment.value > 0) {
    draft.value.equity = counterAmount(value)
  }
}

/** Only worth saying once the pair drifted apart – an edited amount, or a total that moved afterwards. */
const financingGapHint = computed(() => {
  const entered = draft.value.total_financing_amount

  if (totalInvestment.value <= 0 || entered === null || Math.abs(entered - amountToFinance.value) < 0.5) {
    return ''
  }

  return t('payment.calculated', { amount: euro(amountToFinance.value) })
})

const conversionOf = (key: AmountKey): string | null => {
  const other = counterpart(draft.value[key], draft.value[`${key}_unit`], draft.value.purchase_price)

  if (other === null) {
    return null
  }

  return other.unit === 'euro' ? `≈ ${euro(other.value)}` : `≈ ${n(other.value, { maximumFractionDigits: 2 })} %`
}

const modernizationColumns = computed<PositionColumn[]>(() => [
  {
    key: 'type',
    label: t('modernization.columns.type'),
    type: 'select',
    options: props.improvementTypes.map((type) => ({
      value: type,
      label: t(`realEstateImprovement.${type}`),
    })),
  },
  { key: 'price', label: t('modernization.columns.price'), type: 'currency', width: 'w-40', total: true },
])

const loanTypeOptions = computed(() => props.loanTypes.map((type) => ({ value: type, label: t(`loanType.${type}`) })))

const hasInterestTerm = computed(() => draft.value.loan_type !== 'variable')

const addModernization = (): void => {
  draft.value.modernizations = [
    ...draft.value.modernizations,
    { key: crypto.randomUUID(), cells: { type: null, price: null } },
  ]
}

const removeModernization = (key: string): void => {
  draft.value.modernizations = draft.value.modernizations.filter((row) => row.key !== key)
}

const selectLoanType = (): void => {
  if (!hasInterestTerm.value) {
    draft.value.interest_term_years = null
  }
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <PropertyStepErrors :messages="errors" />

    <div class="grid grid-cols-1 gap-4 xl:grid-cols-5">
      <div class="min-w-0 xl:col-span-3">
        <FinestCard :title="t('investment.title')" icon="pi pi-calculator" class="h-full">
          <div class="@container" style="--calc-value: 280px">
            <FinestCalcRow :label="t('fields.purchase_price')" required>
              <FinestInputNumber
                v-model="draft.purchase_price"
                mode="currency"
                :locale="locale"
                :max-fraction-digits="0"
                :state="missingWhen(isMissing(draft.purchase_price))"
                :aria-label="t('fields.purchase_price')"
                class="w-full" />
            </FinestCalcRow>

            <FinestCalcRow
              variant="group"
              sign="+"
              :label="t('groups.additionalCosts')"
              :value="euro(additionalCosts)" />

            <FinestCalcRow
              v-for="key in amountKeys"
              :key="key"
              indent
              :label="t(`fields.${key}`)"
              :required="isRequired(key)"
              :conversion="conversionOf(key)">
              <FinestAmountInput
                v-model="draft[key]"
                v-model:unit="draft[`${key}_unit`]"
                :reference-value="draft.purchase_price"
                :state="missingWhen(isAmountMissing(key))"
                :placeholder="draft[key] === null ? t('addValue') : undefined"
                :aria-label="t(`fields.${key}`)"
                class="w-full" />
            </FinestCalcRow>

            <FinestCalcRow
              variant="group"
              sign="+"
              :label="t('groups.modernization')"
              :value="euro(modernizationCosts)" />

            <div class="pl-3">
              <FinestCustomPositionListCard
                v-model:rows="draft.modernizations"
                plain
                title=""
                :columns="modernizationColumns"
                :add-label="t('modernization.add')"
                editable-rows
                :show-total="false"
                :max-fraction-digits="0"
                @add-row="addModernization"
                @remove-row="removeModernization" />
            </div>

            <FinestCalcRow variant="total" sign="=" :label="t('investment.total')" :value="euro(totalInvestment)" />
          </div>
        </FinestCard>
      </div>

      <div class="min-w-0 xl:col-span-2">
        <FinestCard :title="t('payment.title')" icon="pi pi-wallet" class="h-full">
          <div class="@container" style="--calc-value: 170px">
            <div class="mb-2">
              <div class="flex items-baseline gap-2">
                <p class="section-label section-label--sm">{{ t('investment.total') }}</p>
                <span class="text-ink-3 ml-auto shrink-0 text-sm font-bold tabular-nums">
                  {{ shareOfTotal(totalInvestment) }}
                </span>
              </div>
              <p class="text-brand-blue-1 mt-1 text-2xl font-black tracking-tight tabular-nums">
                {{ euro(totalInvestment) }}
              </p>
            </div>

            <FinestCalcRow :gutter="false" dense :label="t('fields.equity')" required>
              <template #meta>
                <span class="text-brand-teal font-bold">{{ shareOfTotal(draft.equity) }}</span>
              </template>

              <FinestInputNumber
                :model-value="draft.equity"
                mode="currency"
                :locale="locale"
                :max-fraction-digits="0"
                :state="missingWhen(isMissing(draft.equity))"
                :aria-label="t('fields.equity')"
                class="w-full"
                @update:model-value="setEquity" />
            </FinestCalcRow>

            <FinestCalcRow :gutter="false" dense :label="t('fields.total_financing_amount')" required>
              <template #meta>
                <span class="text-brand-blue-1 font-bold">{{ shareOfTotal(draft.total_financing_amount) }}</span>
              </template>

              <FinestInputNumber
                :model-value="draft.total_financing_amount"
                mode="currency"
                :locale="locale"
                :max-fraction-digits="0"
                :state="missingWhen(isMissing(draft.total_financing_amount))"
                :aria-label="t('fields.total_financing_amount')"
                class="w-full"
                @update:model-value="setFinancingAmount" />
            </FinestCalcRow>

            <p v-if="financingGapHint" class="text-ink-3 pr-3 text-right text-xs">{{ financingGapHint }}</p>

            <div class="border-line mt-5 border-t pt-5">
              <p class="section-label section-label--sm">{{ t('loan.terms') }}</p>
              <p class="text-ink-3 mt-1 mb-4 text-xs">{{ t('loan.hint') }}</p>

              <div class="flex flex-col gap-4">
                <FinestField :label="t('fields.loan_type')" input-id="loan-type" required>
                  <FinestSelect
                    id="loan-type"
                    v-model="draft.loan_type"
                    :options="loanTypeOptions"
                    :placeholder="t('selectPlaceholder')"
                    :aria-label="t('fields.loan_type')"
                    :state="missingWhen(isMissing(draft.loan_type))"
                    @change="selectLoanType" />
                </FinestField>

                <div class="grid gap-4" :class="hasInterestTerm ? '@min-[300px]:grid-cols-2' : ''">
                  <FinestField
                    v-if="hasInterestTerm"
                    :label="t('fields.interest_term_years')"
                    input-id="interest-term-years"
                    required>
                    <FinestInputNumber
                      id="interest-term-years"
                      v-model="draft.interest_term_years"
                      :locale="locale"
                      :suffix="t('yearsSuffix')"
                      :max-fraction-digits="0"
                      :use-grouping="false"
                      :state="missingWhen(isMissing(draft.interest_term_years))"
                      :aria-label="t('fields.interest_term_years')" />
                  </FinestField>

                  <FinestField :label="t('fields.repayment_rate')" input-id="repayment-rate" required>
                    <FinestInputNumber
                      id="repayment-rate"
                      v-model="draft.repayment_rate"
                      :locale="locale"
                      suffix=" %"
                      :min-fraction-digits="2"
                      :max-fraction-digits="2"
                      :state="missingWhen(isMissing(draft.repayment_rate))"
                      :aria-label="t('fields.repayment_rate')" />
                  </FinestField>
                </div>
              </div>
            </div>
          </div>
        </FinestCard>
      </div>
    </div>
  </div>
</template>

<i18n>
{
  "de": {
    "investment": {
      "title": "Gesamtinvestition",
      "total": "Gesamtinvestition"
    },
    "payment": {
      "title": "Wie es bezahlt wird",
      "calculated": "Rechnerisch: {amount} (Gesamtinvestition − Eigenkapital)"
    },
    "loan": {
      "terms": "Konditionen",
      "hint": "Diese Konditionen gehen so an die Banken – du kannst sie später noch anpassen."
    },
    "groups": {
      "additionalCosts": "Kaufnebenkosten",
      "modernization": "Modernisierungskosten"
    },
    "fields": {
      "purchase_price": "Kaufpreis",
      "land_register_charge": "Grunderwerbssteuer",
      "broker_commission": "Maklerprovision",
      "notary_land_registry_fees": "Notar- und Grundbuchkosten",
      "equity": "Eigenkapital",
      "loan_type": "Gewünschte Darlehensart",
      "total_financing_amount": "Finanzierungssumme",
      "interest_term_years": "Zinsbindung",
      "repayment_rate": "Tilgung"
    },
    "modernization": {
      "add": "Modernisierung hinzufügen",
      "columns": {
        "type": "Art",
        "price": "Kosten"
      }
    },
    "loanType": {
      "annuity": "Annuitätendarlehen",
      "variable": "Variables Darlehen"
    },
    "selectPlaceholder": "Bitte wählen",
    "yearsSuffix": " Jahre",
    "addValue": "+ eintragen"
  },
  "en": {
    "investment": {
      "title": "Total investment",
      "total": "Total investment"
    },
    "payment": {
      "title": "How it is paid",
      "calculated": "Calculated: {amount} (total investment − equity)"
    },
    "loan": {
      "terms": "Terms",
      "hint": "These terms go to the banks as entered – you can adjust them later."
    },
    "groups": {
      "additionalCosts": "Additional purchase costs",
      "modernization": "Modernisation costs"
    },
    "fields": {
      "purchase_price": "Purchase price",
      "land_register_charge": "Property transfer tax",
      "broker_commission": "Broker commission",
      "notary_land_registry_fees": "Notary & land registry fees",
      "equity": "Equity",
      "loan_type": "Preferred loan type",
      "total_financing_amount": "Financing amount",
      "interest_term_years": "Interest rate period",
      "repayment_rate": "Repayment rate"
    },
    "modernization": {
      "add": "Add modernisation",
      "columns": {
        "type": "Type",
        "price": "Costs"
      }
    },
    "loanType": {
      "annuity": "Annuity loan",
      "variable": "Variable loan"
    },
    "selectPlaceholder": "Please select",
    "yearsSuffix": " years",
    "addValue": "+ add"
  }
}
</i18n>
